package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.saves.SaveOutcome
import com.evoapps.evoshift.saves.isSavedOrKept
import com.evoapps.evoshift.saves.runOrKeepOnDevice
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.FinalizeRosterSkeletonUseCase
import com.evoapps.evoshift.domain.usecase.SendAvailabilityRequestUseCase
import com.evoapps.evoshift.domain.model.AvailabilityEmailLinks
import com.evoapps.evoshift.domain.usecase.finance.GetRevenuePlanUseCase
import com.evoapps.evoshift.domain.usecase.finance.RevenuePlan
import com.evoapps.evoshift.domain.model.capabilitiesAt
import kotlinx.coroutines.async
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterUseCase
import com.evoapps.evoshift.domain.usecase.shift.DeleteShiftUseCase
import com.evoapps.evoshift.domain.usecase.shift.ShiftFactory
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.usecase.ApplyWeeklySkeletonUseCase
import com.evoapps.evoshift.domain.usecase.SkeletonOutcome
import com.evoapps.evoshift.domain.usecase.availability.GetMonthAnswersUseCase
import com.evoapps.evoshift.domain.usecase.availability.MonthAnswers
import com.evoapps.evoshift.domain.schedule.DayAnswerSummary
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.roster_skeleton_empty_templates
import evoshift.composeapp.generated.resources.roster_skeleton_no_week
import evoshift.composeapp.generated.resources.skeleton_filled
import evoshift.composeapp.generated.resources.skeleton_filled_none
import kotlinx.datetime.LocalTime

data class RosterSkeletonUiState(
    val isLoading: Boolean = true,
    val roster: Roster? = null,
    val shiftsByDate: Map<LocalDate, List<Shift>> = emptyMap(),
    val availableShiftTemplates: List<ShiftTemplate> = emptyList(),
    val availableDayTemplates: List<DayTemplate> = emptyList(),
    val error: UiText? = null,
    /** Finalising is in flight: the confirm action shows progress, the grid stays. */
    val isSaving: Boolean = false,
    val success: Boolean = false,
    val firstDayOfWeek: Int = 1,
    /** The workplace's currency, for the revenue plan. */
    val currency: String = "PLN",
    /** The workplace's roles, offered for a custom shift's role (SH-130). */
    val availableRoles: List<String> = emptyList(),
    /** The skeleton is finished; these steps close the process before the month opens (SH-69). */
    val finishStep: SkeletonFinishStep? = null,
    /** The availability request is on its way. */
    val isSendingRequest: Boolean = false,
    /** What the person may do at the roster's workplace: shaping the month needs [Gate.ROSTER_EDITING] (not once the plan ended). */
    val capabilities: Set<Capability> = emptySet(),
    /** Days are being filled from the weekly skeleton (SH-48). */
    val isFilling: Boolean = false,
    /** A one-off note for the snackbar (how many shifts the weekly skeleton added). */
    val message: UiText? = null,
    /**
     * SH-172: the team's answers for the month (asked before or beside the demand), for those who
     * run availability campaigns; null: none to show (not asked, nobody answered, or not theirs).
     */
    val answers: MonthAnswers? = null
) {
    /** SH-172: [date]'s answers against its planned shifts; null when there are none to show. */
    fun dayAnswers(date: LocalDate): DayAnswerSummary? =
        answers?.takeIf { it.shows }?.day(date, shiftsByDate[date].orEmpty())
}

/** What follows a finished skeleton: the month's sales plan, then the question to ask the team for availability. */
sealed interface SkeletonFinishStep {
    data class RevenuePlanSummary(val plan: RevenuePlan) : SkeletonFinishStep
    data object AvailabilityRequest : SkeletonFinishStep
}

sealed interface RosterSkeletonEvent {
    data class AddShiftFromTemplate(val date: LocalDate, val template: ShiftTemplate) : RosterSkeletonEvent

    data class AddCustomShift(
        val date: LocalDate,
        val startTime: LocalTime,
        val endTime: LocalTime,
        val role: String,
        val saveAsTemplate: Boolean,
        val templateName: String
    ) : RosterSkeletonEvent

    data class DeleteShift(val shiftId: String) : RosterSkeletonEvent
    data class ApplyDayTemplate(val date: LocalDate, val template: DayTemplate) : RosterSkeletonEvent
    data object FinalizeSkeleton : RosterSkeletonEvent
    /** The revenue plan has been read. */
    data object RevenuePlanSeen : RosterSkeletonEvent
    data class SendAvailabilityRequest(
        val startDate: String,
        val endDate: String,
        val dueDate: String,
        val message: String?,
        /** SH-173: also by e-mail with a personal link to the web form. */
        val emailLinks: AvailabilityEmailLinks = AvailabilityEmailLinks.NONE
    ) : RosterSkeletonEvent
    /** Not now: the request can be sent later from the Rosters list. */
    data object SkipAvailabilityRequest : RosterSkeletonEvent
    data object DismissError : RosterSkeletonEvent
    /** SH-48: the weekly skeleton for the days of [range] that have no shifts yet. */
    data class FillDays(val range: ScheduleRange) : RosterSkeletonEvent
    data object MessageShown : RosterSkeletonEvent
}

/**
 * Skeleton phase of a roster: the manager shapes the demand (open shifts per day) from templates
 * or by hand, then finalises it into a DRAFT for assignments. Shifts are observed live.
 */
class RosterSkeletonViewModel(
    private val rosterId: String,
    private val getRosterUseCase: GetRosterUseCase,
    private val workplaceRepository: WorkplaceRepository,
    private val shiftRepository: ShiftRepository,
    private val finalizeRosterSkeletonUseCase: FinalizeRosterSkeletonUseCase,
    private val deleteShiftUseCase: DeleteShiftUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRevenuePlanUseCase: GetRevenuePlanUseCase? = null,
    private val sendAvailabilityRequestUseCase: SendAvailabilityRequestUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val applyWeeklySkeleton: ApplyWeeklySkeletonUseCase? = null,
    /** SH-172: the month's answers while the demand is built; null: not shown. */
    private val getMonthAnswers: GetMonthAnswersUseCase? = null,
    /** SH-148: the app's saves: finishing the skeleton goes on even if the screen is left. */
    private val saves: com.evoapps.evoshift.saves.PendingSaves = com.evoapps.evoshift.saves.PendingSaves(),
    /** SH-186: offline, a save unanswered for a few seconds counts as kept on this device. */
    private val connectivity: com.evoapps.evoshift.connectivity.ConnectivityWatcher = com.evoapps.evoshift.connectivity.ManualConnectivity()
) : ViewModel() {

    private val _uiState = MutableStateFlow(RosterSkeletonUiState())
    val uiState: StateFlow<RosterSkeletonUiState> = _uiState.asStateFlow()

    /** The last snapshot of the roster's shifts: what a failed optimistic change goes back to. */
    private var observedShifts: List<Shift> = emptyList()

    init {
        loadData()
    }

    private fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.roster == null) }
            val roster = getRosterUseCase(rosterId).getOrElse { e ->
                _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                return@launch
            }
            val capabilities = capabilitiesAt(getCurrentUserUseCase(), observePlanActive, roster.workplaceId).first()
            _uiState.update { it.copy(capabilities = capabilities) }
            val workplace = workplaceRepository.getWorkplace(roster.workplaceId).getOrNull()
            val shiftTemplates = workplaceRepository.getShiftTemplates(roster.workplaceId).getOrDefault(emptyList())
            val dayTemplates = workplaceRepository.getDayTemplates(roster.workplaceId).getOrDefault(emptyList())
            // SH-172: the answers are the managers' to read (those who ask for them).
            if (Capability.REQUEST_AVAILABILITY in capabilities) {
                launch { loadAnswers(roster) }
            }

            shiftRepository.observeShiftsForRoster(rosterId)
                // SH-188: a failed listener leaves the month on screen with a message (no crash).
                .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
                .collect { shifts ->
                observedShifts = shifts
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        roster = roster,
                        shiftsByDate = shifts.groupBy { shift -> LocalDate.parse(shift.date) },
                        availableShiftTemplates = shiftTemplates,
                        availableDayTemplates = dayTemplates,
                        firstDayOfWeek = workplace?.settings?.firstDayOfWeek ?: 1,
                        currency = workplace?.settings?.currency ?: it.currency,
                        availableRoles = workplace?.settings?.availableRoles ?: emptyList()
                    )
                }
            }
        }
    }

    private suspend fun loadAnswers(roster: Roster) {
        val read = getMonthAnswers ?: return
        read(roster).onSuccess { answers -> _uiState.update { it.copy(answers = answers) } }
    }

    fun onEvent(event: RosterSkeletonEvent) {
        // Read-only once the plan ended (SH-65): only messages are handled.
        val messages = event == RosterSkeletonEvent.DismissError || event == RosterSkeletonEvent.MessageShown
        if (!messages && !_uiState.value.capabilities.shows(Gate.ROSTER_EDITING)) return
        when (event) {
            is RosterSkeletonEvent.AddShiftFromTemplate -> addShifts(listOf(event.date to event.template))
            is RosterSkeletonEvent.AddCustomShift -> addCustomShift(event)
            is RosterSkeletonEvent.DeleteShift -> deleteShifts(listOf(event.shiftId))
            is RosterSkeletonEvent.ApplyDayTemplate -> applyDayTemplate(event.date, event.template)
            RosterSkeletonEvent.FinalizeSkeleton -> finalize()
            RosterSkeletonEvent.RevenuePlanSeen -> afterRevenuePlan()
            is RosterSkeletonEvent.SendAvailabilityRequest -> sendAvailabilityRequest(event)
            RosterSkeletonEvent.SkipAvailabilityRequest -> _uiState.update { it.copy(finishStep = null, success = true) }
            RosterSkeletonEvent.DismissError -> _uiState.update { it.copy(error = null) }
            is RosterSkeletonEvent.FillDays -> fillDays(event.range)
            RosterSkeletonEvent.MessageShown -> _uiState.update { it.copy(message = null) }
        }
    }

    /**
     * Fills the days of [range] that have no shifts from the workplace's weekly skeleton (SH-48);
     * the new shifts arrive through the shifts snapshot, so the grid shows them as they are written.
     */
    private fun fillDays(range: ScheduleRange) {
        val roster = _uiState.value.roster ?: return
        val skeleton = applyWeeklySkeleton ?: return
        if (_uiState.value.isFilling) return
        _uiState.update { it.copy(isFilling = true) }
        viewModelScope.launch {
            skeleton.fillDays(roster.workplaceId, rosterId, roster.year, roster.month, range, observedShifts).fold(
                onSuccess = { outcome ->
                    val note = when (outcome) {
                        is SkeletonOutcome.Copied ->
                            if (outcome.shifts == 0) UiText.Resource(Res.string.skeleton_filled_none)
                            else UiText.Resource(Res.string.skeleton_filled, listOf(outcome.shifts))
                        SkeletonOutcome.NoWeek -> UiText.Resource(Res.string.roster_skeleton_no_week)
                        SkeletonOutcome.EmptyTemplates -> UiText.Resource(Res.string.roster_skeleton_empty_templates)
                    }
                    _uiState.update { it.copy(isFilling = false, message = note) }
                },
                onFailure = { e -> _uiState.update { it.copy(isFilling = false, error = e.toUiText()) } }
            )
        }
    }

    private suspend fun createdBy(): String = getCurrentUserUseCase().first()?.uid ?: "MANAGER"

    /**
     * Replaces the day's shifts with the day template's shifts. Shown at once; the snapshot flow
     * then confirms it, and a failure goes back to the last snapshot (some writes may have landed).
     */
    private fun applyDayTemplate(date: LocalDate, dayTemplate: DayTemplate) {
        val templates = _uiState.value.availableShiftTemplates.associateBy { it.id }
        val toCreate = dayTemplate.shiftTemplateIds.mapNotNull { templates[it] }.map { date to it }
        val toDelete = _uiState.value.shiftsByDate[date].orEmpty().map { it.id }
        viewModelScope.launch {
            val author = createdBy()
            val preview = toCreate.mapIndexed { i, (day, template) ->
                ShiftFactory.fromTemplate(rosterId, day, template, createdBy = author).copy(id = "$PENDING_ID_PREFIX$day-$i")
            }
            _uiState.update { it.copy(shiftsByDate = it.shiftsByDate + (date to preview)) }
            runCatching {
                toDelete.forEach { deleteShiftUseCase(it).getOrThrow() }
                createShifts(toCreate)
            }.onFailure { e ->
                _uiState.update {
                    it.copy(
                        shiftsByDate = observedShifts.groupBy { shift -> LocalDate.parse(shift.date) },
                        error = e.toUiText()
                    )
                }
            }
        }
    }

    private fun addShifts(shifts: List<Pair<LocalDate, ShiftTemplate>>) {
        viewModelScope.launch {
            runCatching { createShifts(shifts) }.onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
        }
    }

    private suspend fun createShifts(shifts: List<Pair<LocalDate, ShiftTemplate>>) {
        if (shifts.isEmpty()) return
        val author = createdBy()
        shiftRepository.createShifts(
            shifts.map { (date, template) -> ShiftFactory.fromTemplate(rosterId, date, template, createdBy = author) }
        ).getOrThrow()
    }

    private fun addCustomShift(event: RosterSkeletonEvent.AddCustomShift) {
        viewModelScope.launch {
            runCatching {
                val roster = _uiState.value.roster ?: error("Roster not loaded")
                val start = event.startTime.toClockString()
                val end = event.endTime.toClockString()
                val role = event.role.takeIf { it.isNotBlank() } // no role: anyone may work it
                if (event.saveAsTemplate && event.templateName.isNotBlank()) {
                    workplaceRepository.createShiftTemplate(
                        roster.workplaceId,
                        ShiftTemplate(id = "", name = event.templateName, startTime = start, endTime = end, role = role, skills = emptyList())
                    ).getOrThrow()
                    val templates = workplaceRepository.getShiftTemplates(roster.workplaceId).getOrDefault(emptyList())
                    _uiState.update { it.copy(availableShiftTemplates = templates) }
                }
                shiftRepository.createShift(
                    ShiftFactory.newShift(rosterId, event.date, start, end, role, createdBy = createdBy())
                ).getOrThrow()
            }.onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
        }
    }

    private fun deleteShifts(shiftIds: List<String>) {
        viewModelScope.launch {
            shiftIds.forEach { id ->
                deleteShiftUseCase(id).onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
            }
        }
    }

    /** Whether the person may ask the team for availability for this month (and nobody has yet). */
    private var mayRequestAvailability = false

    /**
     * Finishes the skeleton: the month becomes a DRAFT. Then, before it opens, the month's revenue
     * plan (for those who see money) and the question to send the availability request (for those
     * who run availability campaigns) close the process (SH-69).
     */
    private fun finalize() {
        if (_uiState.value.isSaving) return
        val roster = _uiState.value.roster
        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true) }
            val capabilities = roster?.let { getCurrentUserUseCase().first()?.capabilitiesAt(it.workplaceId) }.orEmpty()
            val plan = async {
                if (roster == null || Capability.VIEW_FINANCES !in capabilities) null
                else getRevenuePlanUseCase?.invoke(roster.workplaceId, rosterId, roster.year, roster.month)?.getOrNull()
            }
            // SH-186: offline, the roster's new status sits in this device's cache: after a few
            // seconds the skeleton closes as finished and the shell says so.
            val outcome = saves.runOrKeepOnDevice("roster:$rosterId:finalize", connectivity.isOnline) { finalizeRosterSkeletonUseCase(rosterId) }
            if (outcome.isSavedOrKept) {
                mayRequestAvailability = roster != null && roster.availabilityRequestId == null &&
                    sendAvailabilityRequestUseCase != null && Capability.REQUEST_AVAILABILITY in capabilities
                val next = plan.await()?.let { SkeletonFinishStep.RevenuePlanSummary(it) }
                    ?: SkeletonFinishStep.AvailabilityRequest.takeIf { mayRequestAvailability }
                _uiState.update { it.copy(isSaving = false, finishStep = next, success = next == null) }
            } else {
                plan.cancel()
                val e = (outcome as SaveOutcome.Failed).error
                _uiState.update { it.copy(isSaving = false, error = e.toUiText()) }
            }
        }
    }

    private fun afterRevenuePlan() {
        _uiState.update {
            if (mayRequestAvailability) it.copy(finishStep = SkeletonFinishStep.AvailabilityRequest)
            else it.copy(finishStep = null, success = true)
        }
    }

    private fun sendAvailabilityRequest(event: RosterSkeletonEvent.SendAvailabilityRequest) {
        val roster = _uiState.value.roster ?: return
        val send = sendAvailabilityRequestUseCase ?: return
        if (_uiState.value.isSendingRequest) return
        viewModelScope.launch {
            _uiState.update { it.copy(isSendingRequest = true) }
            val managerId = getCurrentUserUseCase().first()?.uid.orEmpty()
            send(
                managerId = managerId,
                workplaceId = roster.workplaceId,
                rosterId = rosterId,
                month = "${roster.year}-${roster.month.toString().padStart(2, '0')}",
                startDate = event.startDate,
                endDate = event.endDate,
                dueDate = event.dueDate,
                message = event.message,
                emailLinks = event.emailLinks
            ).fold(
                onSuccess = { _uiState.update { it.copy(isSendingRequest = false, finishStep = null, success = true) } },
                // The dialog stays: the manager can try again or leave it for later.
                onFailure = { e -> _uiState.update { it.copy(isSendingRequest = false, error = e.toUiText()) } }
            )
        }
    }
}

/** `HH:mm` regardless of seconds, the format shift templates store. */
internal fun LocalTime.toClockString(): String =
    "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
